import { Delete, Get, Patch, Post } from "@/axios/AxiosFunctions";
import Breadcrumb from "@/components/Breadcrumb";
import { Button } from "@/components/Core/Button";
import SearchInput from "@/components/Core/SearchInput";
import SideBarSkeleton from "@/components/Core/SideBarSkeleton";
import TableStructure from "@/components/Core/TableStructure";
import TableActionHandler from "@/components/TableActionHandler";
import { BaseURL } from "@/config/apiUrl";
import { recordsLimit } from "@/constant/constants";
import useDebounce from "@/customHooks/useDebounce";
import { categoryManagementTableHeaders } from "@/data/data";
import { apiHeader, formatDate, RenderToast } from "@/helper/HelperFunction";
import AddEditCategoryModal from "@/modals/AddEditCategoryModal";
import AreYouSureModal from "@/modals/AreYouSureModal";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import classes from "./CategoryManagement.module.css";

const MODAL_STATES = {
  DELETE_CATEGORY: "DELETE_CATEGORY",
  CREATE_CATEGORY: "CREATE_CATEGORY",
  EDIT_CATEGORY: "EDIT_CATEGORY",
};

const CategoryLoadingStates = {
  GET_CATEGORY: "GET_CATEGORY",
  DELETE_CATEGORY: "DELETE_CATEGORY",
  SUBMIT_CATEGORY: "SUBMIT_CATEGORY",
};

export default function CategoryManagement() {
  const { access_token } = useSelector((state) => state.authReducer);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRecord, setTotalRecord] = useState(0);
  const [showModal, setShowModal] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  // get category
  const getData = async ({ _page = page, _search = debouncedSearch }) => {
    const queryParams = {
      page: _page,
      limit: recordsLimit,
      search: _search,
    };
    const apiUrl = BaseURL(
      `categories/admin/all?${new URLSearchParams(queryParams).toString()}`
    );
    setIsLoading(CategoryLoadingStates.GET_CATEGORY);
    const response = await Get(apiUrl, access_token);
    if (response) {
      setData(response?.data?.data?.categories);
      setTotalRecord(response?.data?.data?.totalCount);
    }
    setIsLoading(null);
  };
  //   add edit brand
  const handleAddEditCategory = async (params) => {
    const apiUrl = BaseURL(`categories`);
    setIsLoading(CategoryLoadingStates.SUBMIT_CATEGORY);
    const response = selectedData
      ? await Patch(apiUrl, params, apiHeader(access_token))
      : await Post(apiUrl, params, apiHeader(access_token));
    if (response) {
      RenderToast({
        type: "success",
        message: selectedData
          ? "Category updated successfully"
          : "Category added successfully",
      });
      if (selectedData) {
        setData(
          data?.map((item) => {
            if (item?._id === selectedData?._id) {
              return { ...item, ...params };
            }
            return item;
          })
        );
      } else {
        setData([response?.data?.data, ...data]);
      }
      setShowModal(null);
      setSelectedData(null);
    }
    setIsLoading(null);
  };
  //   delete brand
  const handleDeleteCategory = async () => {
    const apiUrl = BaseURL(`categories/${selectedData?._id}`);
    setIsLoading(CategoryLoadingStates.DELETE_CATEGORY);
    const response = await Delete(apiUrl, apiHeader(access_token));
    if (response) {
      RenderToast({
        type: "success",
        message: "Category deleted successfully",
      });
      setData(data?.filter((item) => item?._id !== selectedData?._id));
      setTotalRecord((prev) => prev - 1);
      setSelectedData(null);
      setShowModal(null);
    }
    setIsLoading(null);
  };

  useEffect(() => {
    getData({
      _page: 1,
    });
  }, [debouncedSearch]);

  return (
    <SideBarSkeleton>
      <div className={classes.mainWrapper}>
        <div className={classes.header}>
          <div className={classes.headerTitle}>
            <h3>Category Management</h3>
            <Breadcrumb />
          </div>
        </div>
        <div className={classes.searchWrapper}>
          <SearchInput
            placeholder="Search Category..."
            customStyle={{
              height: "40px",
            }}
            value={search}
            setter={setSearch}
          />
          <Button
            onClick={() => {
              setShowModal(MODAL_STATES.CREATE_CATEGORY);
            }}
            variant="primary"
            label="Create Category"
          />
        </div>

        <TableStructure
          headerTitle=""
          tableHeaders={categoryManagementTableHeaders}
          isLoading={isLoading === CategoryLoadingStates.GET_CATEGORY}
          tableContent={data?.map((item) => ({
            ...item,
            createdAt: formatDate(item?.createdAt),
            actions: (
              <TableActionHandler
                onEdit={() => {
                  setSelectedData(item);
                  setShowModal(MODAL_STATES.EDIT_CATEGORY);
                }}
                onDelete={() => {
                  setSelectedData(item);
                  setShowModal(MODAL_STATES.DELETE_CATEGORY);
                }}
              />
            ),
          }))}
          page={page}
          setPage={(e) => {
            setPage(e);
            getData({
              _page: e,
            });
          }}
          totalRecord={totalRecord}
          recordLimit={recordsLimit}
          noDataMessage="No Categories Found"
          customStyle={{
            height: "calc(100vh - 294px)",
          }}
        />
        {[MODAL_STATES.CREATE_CATEGORY, MODAL_STATES.EDIT_CATEGORY].includes(
          showModal
        ) && (
          <AddEditCategoryModal
            show={[
              MODAL_STATES.CREATE_CATEGORY,
              MODAL_STATES.EDIT_CATEGORY,
            ].includes(showModal)}
            setShow={() => {
              setShowModal(null);
              setSelectedData(null);
            }}
            selectedItem={selectedData}
            handleClick={handleAddEditCategory}
            modalLoading={isLoading === CategoryLoadingStates.SUBMIT_CATEGORY}
          />
        )}
        {showModal === MODAL_STATES.DELETE_CATEGORY && (
          <AreYouSureModal
            show={showModal}
            setShow={() => {
              setShowModal(null);
              setSelectedData(null);
            }}
            title="Delete Category"
            subTitle="Are you sure you want to delete this category?"
            onClick={handleDeleteCategory}
            isApiCall={isLoading === CategoryLoadingStates.DELETE_CATEGORY}
          />
        )}
      </div>
    </SideBarSkeleton>
  );
}
